<template>
<ComDialog :isShow="visible" :width="800" :title="title" alignCenter @close="handleClose">
   <!-- 应用列表展示 -->
      <div class="selectDevice">
         <span>当前选中设备【{{ deviceCodes.length }}】：</span>
        <el-tag size="small" type="primary" v-for="item in deviceCodes.slice(0, 20)" :key="item.cloudMachineNumber">{{ item.cloudMachineNumber }}</el-tag>
        <span v-show="deviceCodes.length > 20">...</span>
      </div>

      <div class="app-list-container">
        <div class="app-list-header">
          <span class="title">应用列表</span>
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索应用名称"
            :suffix-icon="Search" 
            class="search-input"
            clearable
            @keyup.enter="handleSearch"
            @blur="handleSearch"
          />
        </div>
        <div class="app-list-content" v-loading="loadingAppList">
          <div v-if="appList.length > 0" class="app-grid">
            <!-- 点击应用图标选中 -->
            <div v-for="app in appList" :key="app.fileMd5" class="app-grid-item" @click="selectApp(app)">
              <!-- 根据操作类型选择使用单选还是多选 -->
              <div class="app-selector">
                <el-checkbox  v-model="selectedAppIds" :label="app.fileMd5" @click.stop></el-checkbox>
              </div>
              <!-- 悬浮显示完整包名和版本号 -->
              <el-tooltip placement="top" :show-after="300">
                <div class="app-info">
                  <div class="app-icon" >
                    <img :src="app.logo" alt="app logo" />
                  </div>
                  <div class="app-name">{{ app.appName }}</div>
                  <div class="app-package">{{ app.packageName || '-' }}</div>
                  <div class="app-version">{{ app.version || '-' }}</div>
                </div>
                <template #content>
                  <div>包名：{{ app.packageName || '-' }}</div>
                  <div>版本：{{ app.version || '-' }}</div>
                </template>
              </el-tooltip>
            </div>
          </div>
          <div v-else class="empty-app-list">
            <div>{{ loadingAppList ? '加载中...' : '暂无应用数据' }}</div>
          </div>
        
        </div>
            <!-- 分页组件 -->
            <div class="pagination" v-if="appList.length > 0">
                <el-pagination
                    v-model:current-page="pagination.currentPage"
                    v-model:page-size="pagination.pageSize"
                    :page-sizes="[10,20,50,100]"
                    size="small"
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="pagination.total"
                    @size-change="handlePageSizeChange"
                    @current-change="handleCurrentPageChange"
                />
            </div>
      </div>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" :loading="loading" :disabled="loading" @click="handleConfirm">确认</el-button>
      </div>
  </ComDialog>
</template>

<script setup>
import { ref, computed, defineExpose } from 'vue'
import ComDialog from "@/components/comDialog/index.vue";
import request from "@/utils/request";
import API_CONFIG from "./api.json";
import { ElMessage } from 'element-plus'
import { de } from 'element-plus/es/locales.mjs';

const visible = ref(false)
const deviceCodes = ref([])
const loadingAppList = ref(false)
const selectedAppIds = ref([])
const searchKeyword = ref('')
const appList = ref([])
const loading = ref(false)
const opType = ref('install')
// 分页信息
const pagination = ref({
  currentPage: 1,
  pageSize: 20,
  total: 0
})

const title = computed(() => {
  return opType.value == 'install'? '批量安装应用' : '批量卸载应用'
})

// 计算当前选中的应用对象
const selectedApps = computed(() => {
  // 多选模式
  if (!selectedAppIds.value || selectedAppIds.value.length === 0) return []
  return appList.value.filter(app => selectedAppIds.value.includes(app.fileMd5))
})

const loadApps = async () => {
  loadingAppList.value = true
  request.post(API_CONFIG.queryUrl.url, {
    pageDomain:{
        pageNum: pagination.value.currentPage,
        pageSize: pagination.value.pageSize
    },
    appName: searchKeyword.value
  })
    .then((res) => {
      pagination.value.total = res.data.total
      if (res.code === 200&& res.data && Array.isArray(res.data.data)) {
        appList.value = res.data.data || []
        pagination.value.total = res.data.total || 0
      } else {
        appList.value = []
        selectedAppIds.value = []
      }
    })
    .catch((e) => {
      ElMessage.error('获取应用列表失败：' + e.message)
      appList.value = []
      selectedAppIds.value = []
    })
    .finally(() => {
      loadingAppList.value = false
    })
}

const selectApp = (app) => {
    // 多选模式下，点击应用图标切换选中状态
    const index = selectedAppIds.value.indexOf(app.fileMd5)
    if (index == -1) {
      selectedAppIds.value.push(app.fileMd5)
    } else {
      selectedAppIds.value.splice(index, 1)
    }
}

const handleClose = () => {
  visible.value = false
  selectedApps.value = []
  searchKeyword.value = ''
}

// 重绑代理并更新应用白名单
const handleConfirm = () => {
    loading.value = true
    const url = opType.value =='install'?API_CONFIG.installUrl.url:API_CONFIG.uninstallUrl.url
    const params = {
      ctrAliaList: deviceCodes.value.map(item => item.cloudMachineNumber)
    }
    if(opType.value =='install'){
      params.md5List = selectedApps.value.map(item => item.fileMd5)
    }   
    if(opType.value =='uninstall'){
      params.packageNames = selectedApps.value.map(item => item.packageName)
    }
    request.post(url, params)
    .then((res) => {
      if (res.code == 0) {
        ElMessage.success('操作成功，请5-10分钟之后进入云机查看安装结果')
        handleClose()
      } else {
        ElMessage.error('操作失败：' + res.msg || '未知错误')
      }
    })
    .catch((e) => {
      ElMessage.error('操作失败：' + e.message)
    })
    .finally(() => {
        loading.value = false
    })
}

const handleSearch = () => {
  pagination.value.currentPage = 1
  loadApps()
}

// 处理分页大小变化
const handlePageSizeChange = (size) => {
  pagination.value.pageSize = size
  pagination.value.currentPage = 1
  loadApps()
}

// 处理当前页码变化
const handleCurrentPageChange = (current) => {
  pagination.value.currentPage = current
    loadApps()
}

const open = (deviceList, type) => {
  opType.value = type 
  deviceCodes.value = deviceList
  visible.value = true
  loadApps()
}

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
.selectDevice{
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.content {
  font-size: 14px;
  width: 100%;
  text-align: left;
  padding: 20px;
}

.app-list-container {
  margin-top: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.app-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
  padding: 10px;
  .title {
    font-size: 14px;
    font-weight: 500;
  }
}

.search-input {
  width: 200px;
}

.app-list-content {
  max-height: 400px;
  overflow-y: auto;
  font-size: 14px;
}

.app-grid {
  padding: 10px;
  max-height: 45vh;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 15px;
}

.app-grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s;
  position: relative;
  &:hover {
    background-color: #f5f7fa;
  }
}

.app-selector {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;
}

.app-icon {
  width: 40px;
  height: 40px; 
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }
}

.app-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.app-name {
  font-size: 12px;
  color: #333;
  text-align: center;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-package,
.app-version {
  font-size: 11px;
  color: #999;
  text-align: center;
  width: 100%;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.el-checkbox__label),
:deep(.el-radio__label){
  display: none!important;
}

.empty-app-list {
  display: flex;
  width:   100%;
  flex-direction: column;
  justify-content: center;
  padding: 30px;
  color: #999;
  >div{
    text-align: center;
  }
}
.pagination {
  padding: 15px;
}
.op-confirm-btn {
  text-align: center;
  padding: 20px 0;

  .el-button {
    width: 130px;
    height: 38px;
    border-radius: 20px;
    &:hover {
      opacity: 0.8;
    }
  }
}

.warning-tips {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 15px;
  display: block;
}
</style>